import React, { useState, useEffect } from 'react';
import { Zap } from 'lucide-react';
import { api } from '../api';
import { renderStars } from '../utils/renderStars';
import { useTranslation } from 'react-i18next';

const TrainerAnalytics = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.request('/analytics/my-trainer-stats').then(d => { setStats(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center h-64 items-center"><div className="animate-spin w-12 h-12 border-4 border-levelup-accent border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-4" data-testid="trainer-analytics-page">
      <div className="bg-levelup-dark rounded-xl p-4 text-white">
        <h1 className="text-xl font-bold">{t('trainerAnalytics.title')}</h1>
        <p className="text-gray-300 mt-1">{t('trainerAnalytics.description')}</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-levelup-dark" data-testid="trainer-stat-workshops">
          <p className="text-gray-500 text-sm">{t('trainerAnalytics.workshopsDelivered')}</p>
          <p className="text-3xl font-bold text-levelup-dark mt-1">{stats?.completed_workshops || 0}</p>
          <p className="text-xs text-gray-400 mt-1">{stats?.total_workshops || 0} {t('trainerAnalytics.totalScheduled')}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-levelup-teal" data-testid="trainer-stat-learners">
          <p className="text-gray-500 text-sm">{t('trainerAnalytics.learnersTrained')}</p>
          <p className="text-3xl font-bold text-levelup-dark mt-1">{stats?.total_learners_trained || 0}</p>
          <p className="text-xs text-gray-400 mt-1">{t('trainerAnalytics.acrossAllWorkshops')}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-yellow-400" data-testid="trainer-stat-feedback">
          <p className="text-gray-500 text-sm">{t('trainerAnalytics.avgFeedbackScore')}</p>
          <p className="text-3xl font-bold text-levelup-dark mt-1">{stats?.avg_feedback_score || 0}<span className="text-lg text-gray-400">/5</span></p>
          <div className="flex mt-1">{renderStars(Math.round(stats?.avg_feedback_score || 0))}</div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-purple-500" data-testid="trainer-stat-areas">
          <p className="text-gray-500 text-sm">{t('trainerAnalytics.solutionAreas')}</p>
          <p className="text-3xl font-bold text-levelup-dark mt-1">{stats?.solution_areas_covered || 0}</p>
          <p className="text-xs text-gray-400 mt-1">{t('trainerAnalytics.coveredInTraining')}</p>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-levelup-dark flex items-center gap-2"><Zap size={20} className="text-levelup-accent" /> {t('trainerAnalytics.impactScore')}</h2>
            <p className="text-sm text-gray-500 mt-1">{t('trainerAnalytics.impactFormula')}</p>
          </div>
          <div className="text-right">
            <p className="text-5xl font-bold text-levelup-teal">{stats?.impact_score || 0}</p>
            <div className="flex justify-end mt-1">{renderStars(stats?.stars || 0)}</div>
          </div>
        </div>
        {stats?.best_workshop && (
          <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm font-semibold text-amber-800">{t('trainerAnalytics.personalBest')}</p>
            <p className="text-sm text-amber-700">{stats.best_workshop.attendees} {t('trainerAnalytics.attendeesOn')} {stats.best_workshop.date}</p>
          </div>
        )}
      </div>
      {stats?.by_solution_area?.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-4">
          <h2 className="text-lg font-semibold text-levelup-dark mb-4">{t('trainerAnalytics.breakdownBySolutionArea')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('trainerAnalytics.solutionArea')}</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('trainerAnalytics.workshops')}</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('trainerAnalytics.learnersTrained')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {stats.by_solution_area.map((sa, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-levelup-dark">{sa.solution_area}</td>
                    <td className="px-3 py-2">{sa.workshops}</td>
                    <td className="px-3 py-2"><span className="font-bold text-levelup-teal">{sa.learners}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainerAnalytics;
